import { useSelector } from 'react-redux';
import {
  selectAdminId,
  selectAllPermissions,
  selectEmail,
  selectHasAllPermissions,
  selectHasAnyPermissions,
  selectHasPermission,
  selectUserDetails,
} from 'redux/user/user.selectors';
import { isAuthorized } from 'util/authorization';

/**
 * Returns true if the logged in user has any of the permissions passed in
 */
export const useHasAnyPermission = (any: string[]) => {
  return useSelector(state => selectHasAnyPermissions(state, any));
};

/**
 * Returns true if the logged in user has all of the permissions passed in
 */
export const useHasAllPermissions = (all: string[]) => {
  return useSelector(state => selectHasAllPermissions(state, all));
};

/**
 * Returns true if the logged in user has the permission passed in
 */
export const useHasPermission = (permission: string) => {
  return useSelector(state => selectHasPermission(state, permission));
};

/**
 * Returns true if the logged in user id matches one of the authorized user ids
 * passed in
 */
export const useIsAuthorizedUser = (authorizedUserIds: string[]) => {
  const userId = useSelector(selectAdminId);
  return authorizedUserIds.includes(userId);
};

/**
 * Returns the options based on whether the user has the required permissions.
 */
export const useAuthorizedOptions = (options: any) => {
  const allPermissions = useSelector(selectAllPermissions);

  return options?.filter(option => {
    const { permission = '', all = [], any = [] } = option;

    return isAuthorized({ permission: permission, all, any }, allPermissions);
  });
};

export const useHasAllMerchantLocationAccess = () => {
  const userDetails = useSelector(selectUserDetails);

  return (
    !!userDetails?.isSuperAdmin ||
    !!userDetails?.isClientAdmin ||
    !!userDetails?.isWorkspaceAdmin
  );
};

export const useIsIncentivioUser = () => {
  const email = useSelector(selectEmail);
  return email?.endsWith('@incentivio.com') ?? false;
};

export const useHasAccessTier = (tier: 'super' | 'client' | 'workspace') => {
  const userDetails = useSelector(selectUserDetails);

  if (tier === 'super') {
    return !!userDetails?.isSuperAdmin;
  }

  if (tier === 'client') {
    return !!userDetails?.isClientAdmin || !!userDetails?.isSuperAdmin;
  }

  if (tier === 'workspace') {
    return (
      !!userDetails?.isWorkspaceAdmin ||
      !!userDetails?.isClientAdmin ||
      !!userDetails?.isSuperAdmin
    );
  }

  return false;
};
